import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import EditableListItem from '../components/EditableListItem';
import ItemActions from '../components/ItemActions';
import { TaskKanbanView, TaskListView } from '../components/GravityPlan';
import ResearchTab from '../components/ResearchTab';
import { ResearchEntityType } from '../services/researchService';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import { MasterPlanTask, masterPlanTaskService } from '../services/masterPlan';
import { okrService, OrganizationOKR } from '../services/okrs';
import AccountPlanningDashboard from '../components/AccountPlanningDashboard';
import { formatCurrency } from '../utils/currency';
import {
  formatCompanyStatus,
  formatOpportunityStatus,
} from '../utils/enumFormatting';
import { COMPANY_STATUSES, OPPORTUNITY_STATUSES } from '../constants/enums';
import { getAuthHeaders } from '../utils/auth';
import StakeholderMap from '../components/contacts/StakeholderMap';
import OrgChart from '../components/contacts/OrgChart';
import { API_URL } from '../constants/urls';

// Helper component for adding/editing items
interface ItemFormProps {
  label: string;
  onSave: (data: any) => void;
  onCancel?: () => void;
  fields: {
    name: string;
    label: string;
    type?: string;
    required?: boolean;
    options?: { value: string; label: string }[];
  }[];
  initialData?: any;
  isEdit?: boolean;
}

function ItemForm({
  label,
  onSave,
  onCancel,
  fields,
  initialData = {},
  isEdit = false,
}: ItemFormProps) {
  const [isOpen, setIsOpen] = useState(isEdit);
  const [formData, setFormData] = useState<any>(initialData);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
    if (!isEdit) {
      setFormData({});
      setIsOpen(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    const newFormData = { ...formData, [field]: value };

    // Auto-update probability when status changes
    if (field === 'status') {
      if (value === 'won') {
        newFormData.probability = 100;
      } else if (value === 'lost') {
        newFormData.probability = 0;
      }
    }

    setFormData(newFormData);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      setIsOpen(false);
      setFormData({});
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700"
      >
        + Add {label}
      </button>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-gray-50 rounded-lg p-4 space-y-3"
    >
      {fields.map((field) => (
        <div key={field.name}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {field.label}
          </label>
          {field.type === 'textarea' ? (
            <textarea
              value={formData[field.name] || ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              required={field.required}
            />
          ) : field.type === 'date' ? (
            <input
              type="date"
              value={formData[field.name] || ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required={field.required}
            />
          ) : field.type === 'select' && field.options ? (
            <select
              value={formData[field.name] || ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required={field.required}
            >
              <option value="">Select...</option>
              {field.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : field.type === 'number' ? (
            <input
              type="number"
              value={formData[field.name] || ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required={field.required}
              min={field.name === 'probability' ? 0 : undefined}
              max={field.name === 'probability' ? 100 : undefined}
              step={field.name === 'probability' ? 0.1 : undefined}
            />
          ) : (
            <input
              type="text"
              value={formData[field.name] || ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required={field.required}
            />
          )}
        </div>
      ))}
      <div className="flex gap-2">
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          {isEdit ? 'Save' : 'Add'}
        </button>
        <button
          type="button"
          onClick={handleCancel}
          className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function CompanyDetailTailwind() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOrg, canEdit, canDelete } = useOrganization();
  const { token } = useAuth();
  const [company, setCompany] = useState<any>(null);
  const [contacts, setContacts] = useState<any[]>([]);
  const [opportunities, setOpportunities] = useState<any[]>([]);
  const [testimonials, setTestimonials] = useState<any[]>([]);
  const [caseStudies, setCaseStudies] = useState<any[]>([]);
  const [awards, setAwards] = useState<any[]>([]);
  const [propositions, setPropositions] = useState<any[]>([]);
  const [segments, setSegments] = useState<any[]>([]);
  const [organization, setOrganization] = useState<any>(null);
  const [editingCompany, setEditingCompany] = useState(false);

  // Get initial tab from URL hash
  const getInitialTab = () => {
    const hash = location.hash.replace('#', '');
    const validTabs = ['overview', 'contacts', 'planning', 'tasks', 'research'];
    return validTabs.includes(hash) ? (hash as any) : 'overview';
  };

  const [activeTab, setActiveTab] = useState<
    'overview' | 'contacts' | 'tasks' | 'planning' | 'research'
  >(getInitialTab());
  const [tasks, setTasks] = useState<MasterPlanTask[]>([]);
  const [taskViewMode, setTaskViewMode] = useState<'kanban' | 'list'>('kanban');
  const [organizationMembers, setOrganizationMembers] = useState<any[]>([]);
  const [allOKRs, setAllOKRs] = useState<OrganizationOKR[]>([]);
  const [accountPlan, setAccountPlan] = useState<any>(null);

  // Function to handle account plan updates from the dashboard
  const handleAccountPlanUpdate = (updatedPlan: any) => {
    setAccountPlan(updatedPlan);
  };
  const [companyForm, setCompanyForm] = useState({
    name: '',
    description: '',
    website: '',
    linkedin_url: '',
    employee_count: '',
    annual_revenue: '',
    status: 'suspect',
    strategic_owner_id: '',
    segment_ids: [] as string[],
  });
  // Modal states
  const [showContactForm, setShowContactForm] = useState(false);
  const [showOpportunityForm, setShowOpportunityForm] = useState(false);
  const [showTestimonialForm, setShowTestimonialForm] = useState(false);
  const [showCaseStudyForm, setShowCaseStudyForm] = useState(false);
  const [showAwardForm, setShowAwardForm] = useState(false);

  // Editing states
  const [editingContact, setEditingContact] = useState<any>(null);
  const [editingOpportunity, setEditingOpportunity] = useState<any>(null);
  const [editingTestimonial, setEditingTestimonial] = useState<any>(null);
  const [editingCaseStudy, setEditingCaseStudy] = useState<any>(null);
  const [editingAward, setEditingAward] = useState<any>(null);

  // Form data states
  const [contactForm, setContactForm] = useState({
    first_name: '',
    last_name: '',
    title: '',
    email: '',
    phone: '',
    department: '',
    role_type: '',
    influence_level: '',
    relationship_strength: 0,
    reports_to_contact_id: '',
    hierarchy_level: 0,
    notes: '',
  });
  const [contactSearch, setContactSearch] = useState('');
  const [contactSortBy, setContactSortBy] = useState('last_name');
  const [contactSortOrder, setContactSortOrder] = useState<'asc' | 'desc'>(
    'asc',
  );
  const [contactsView, setContactsView] = useState<
    'list' | 'stakeholder' | 'orgchart'
  >('list');

  const [testimonialForm, setTestimonialForm] = useState({
    testimonial_text: '',
    author_name: '',
    author_title: '',
    proposition_id: '',
  });

  const [caseStudyForm, setCaseStudyForm] = useState({
    title: '',
    description: '',
    published_date: '',
    proposition_id: '',
  });

  const [awardForm, setAwardForm] = useState({
    award_name: '',
    awarding_body: '',
    date_won: '',
    description: '',
    proposition_id: '',
  });

  // Track previous organization to detect changes
  const prevOrgRef = React.useRef<string | null>(null);

  // Handle tab changes - update URL hash
  const handleTabChange = (
    tab: 'overview' | 'contacts' | 'planning' | 'tasks' | 'research',
  ) => {
    setActiveTab(tab);
    window.location.hash = tab;
  };

  // Listen for hash changes (browser back/forward)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      const validTabs = ['overview', 'planning', 'tasks', 'research'];
      if (validTabs.includes(hash)) {
        setActiveTab(hash as any);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    if (!id || !selectedOrg || !token) return;

    // If organization changed, navigate to companies list
    if (
      prevOrgRef.current &&
      selectedOrg.id &&
      prevOrgRef.current !== selectedOrg.id
    ) {
      navigate('/companies');
      return;
    }

    // Update the ref for next render
    if (selectedOrg.id) {
      prevOrgRef.current = selectedOrg.id;
    }
    const orgId = selectedOrg.id;

    // Fetch complete company data in a single request (optimized endpoint)
    axios
      .get(`${API_URL}/companies/${id}/complete?organization_id=${orgId}`, {
        headers: getAuthHeaders(),
      })
      .then((res) => {
        const data = res.data;

        // Set all company-related data from single response
        // Merge metrics into company object for display
        setCompany({ ...data.company, ...data.metrics });
        setContacts(data.contacts);
        setOpportunities(data.opportunities);
        setTestimonials(data.testimonials);
        setCaseStudies(data.case_studies);
        setAwards(data.awards);

        // Initialize edit form
        setCompanyForm({
          name: data.company.name || '',
          description: data.company.description || '',
          website: data.company.website || '',
          linkedin_url: data.company.linkedin_url || '',
          employee_count: data.company.employee_count || '',
          annual_revenue: data.company.annual_revenue || '',
          status: data.company.status || 'suspect',
          strategic_owner_id: data.company.strategic_owner_id || '',
          segment_ids: data.company.segments?.map((s: any) => s.id) || [],
        });
      })
      .catch((_err) => {
        // Error fetching complete company data
      });

    // Fetch dropdowns in parallel (independent of company data)
    Promise.all([
      axios.get(`${API_URL}/organizations/${orgId}`, {
        headers: getAuthHeaders(),
      }),
      axios.get(`${API_URL}/propositions?organization_id=${orgId}`, {
        headers: getAuthHeaders(),
      }),
      axios.get(`${API_URL}/segments?organization_id=${orgId}`, {
        headers: getAuthHeaders(),
      }),
      axios.get(`${API_URL}/organizations/${orgId}/members`, {
        headers: getAuthHeaders(),
      }),
    ])
      .then(([orgRes, propsRes, segsRes, membersRes]) => {
        setOrganization(orgRes.data);
        setPropositions(propsRes.data);
        setSegments(segsRes.data);
        setOrganizationMembers(membersRes.data);
      })
      .catch((_err) => {
        // Error fetching dropdowns
      });
  }, [id, selectedOrg, token, navigate]);

  // Lazy load tasks and OKRs when tasks tab is first opened
  useEffect(() => {
    if (activeTab === 'tasks' && selectedOrg && id && tasks.length === 0) {
      const orgId = selectedOrg.id;

      // Fetch tasks for this company
      masterPlanTaskService
        .list(orgId, {
          company_ids: [id],
          include_org_tasks: false,
        })
        .then((res) => setTasks(res))
        .catch((_err) => {
          // Error fetching tasks
        });

      // Fetch OKRs for task alignment
      okrService
        .listOKRs(orgId)
        .then((okrs) => {
          const activeOKRs = okrs.filter(
            (okr) => okr.status === 'draft' || okr.status === 'active',
          );
          setAllOKRs(activeOKRs);
        })
        .catch((_err) => {
          setAllOKRs([]);
        });
    }
  }, [activeTab, selectedOrg, id, tasks.length]);

  const handleSaveCompany = async () => {
    if (!company || !selectedOrg) return;

    try {
      const updateData = {
        ...companyForm,
        annual_revenue:
          companyForm.annual_revenue === ''
            ? null
            : Number(companyForm.annual_revenue),
        employee_count:
          companyForm.employee_count === ''
            ? null
            : Number(companyForm.employee_count),
        strategic_owner_id:
          companyForm.strategic_owner_id === ''
            ? null
            : companyForm.strategic_owner_id,
      };

      // console.log removed
      await axios.put(
        `${API_URL}/companies/${company.id}?organization_id=${selectedOrg.id}`,
        updateData,
        { headers: getAuthHeaders() },
      );
      // Re-fetch company data to get updated financial metrics
      const companyResponse = await axios.get(
        `${API_URL}/companies/${company.id}?organization_id=${selectedOrg.id}`,
        { headers: getAuthHeaders() },
      );
      setCompany(companyResponse.data);
      // Update the form state with the saved data
      setCompanyForm({
        name: companyResponse.data.name || '',
        description: companyResponse.data.description || '',
        website: companyResponse.data.website || '',
        linkedin_url: companyResponse.data.linkedin_url || '',
        employee_count: companyResponse.data.employee_count || '',
        annual_revenue: companyResponse.data.annual_revenue || '',
        status: companyResponse.data.status || 'suspect',
        strategic_owner_id: companyResponse.data.strategic_owner_id || '',
        segment_ids: companyResponse.data.segments?.map((s: any) => s.id) || [],
      });
      setEditingCompany(false);
    } catch (error: any) {
      // console.error removed
      // console.error removed
      alert(
        `Failed to save company: ${error.response?.data?.detail || error.message || 'Unknown error'}`,
      );
    }
  };

  const handleAddTestimonial = async (data: any) => {
    if (!company || !selectedOrg) return;

    // Clean up data - convert empty strings to null for optional fields
    const cleanData = {
      ...data,
      proposition_id: data.proposition_id || null,
      author_name: data.author_name || null,
      author_title: data.author_title || null,
      date_received: data.date_received || null,
    };
    try {
      await axios.post(
        `${API_URL}/companies/${company.id}/testimonials?organization_id=${selectedOrg.id}`,
        cleanData,
        { headers: getAuthHeaders() },
      );
      // Re-fetch testimonials to get proposition_name
      const testimonialsResponse = await axios.get(
        `${API_URL}/companies/${company.id}/testimonials?organization_id=${selectedOrg.id}`,
        { headers: getAuthHeaders() },
      );
      setTestimonials(testimonialsResponse.data);
    } catch (error) {
      // console.error removed
      alert('Failed to add testimonial. Please try again.');
    }
  };

  const handleAddCaseStudy = async (data: any) => {
    if (!company || !selectedOrg) return;

    // Clean up data - convert empty strings to null for optional fields
    const cleanData = {
      ...data,
      proposition_id: data.proposition_id || null,
      description: data.description || null,
      published_date: data.published_date || null,
    };
    // console.log removed
    try {
      await axios.post(
        `${API_URL}/companies/${company.id}/case-studies?organization_id=${selectedOrg.id}`,
        cleanData,
        { headers: getAuthHeaders() },
      );
      // Re-fetch case studies to get proposition_name
      const caseStudiesResponse = await axios.get(
        `${API_URL}/companies/${company.id}/case-studies?organization_id=${selectedOrg.id}`,
        { headers: getAuthHeaders() },
      );
      setCaseStudies(caseStudiesResponse.data);
    } catch (error: any) {
      // console.error removed
      // console.error removed
      alert(
        `Failed to add case study: ${error.response?.data?.detail || error.message || 'Unknown error'}`,
      );
    }
  };

  const handleAddAward = async (data: any) => {
    if (!company || !selectedOrg) return;

    // Clean up data - convert empty strings to null for optional fields
    const cleanData = {
      ...data,
      proposition_id: data.proposition_id || null,
      awarding_body: data.awarding_body || null,
      date_won: data.date_won || null,
      description: data.description || null,
    };
    try {
      await axios.post(
        `${API_URL}/companies/${company.id}/awards?organization_id=${selectedOrg.id}`,
        cleanData,
        { headers: getAuthHeaders() },
      );
      // Re-fetch awards to get proposition_name
      const awardsResponse = await axios.get(
        `${API_URL}/companies/${company.id}/awards?organization_id=${selectedOrg.id}`,
        { headers: getAuthHeaders() },
      );
      setAwards(awardsResponse.data);
    } catch (error) {
      // console.error removed
      alert('Failed to add award. Please try again.');
    }
  };

  // Update handlers for testimonials, case studies, and awards
  const handleUpdateTestimonial = async (data: any) => {
    if (!editingTestimonial || !selectedOrg) return;

    // Clean up data - convert empty strings to null for optional fields
    const cleanData = {
      ...data,
      proposition_id: data.proposition_id || null,
      author_name: data.author_name || null,
      author_title: data.author_title || null,
      date_received: data.date_received || null,
    };
    try {
      await axios.put(
        `${API_URL}/companies/testimonials/${editingTestimonial.id}?organization_id=${selectedOrg.id}`,
        cleanData,
        { headers: getAuthHeaders() },
      );
      // Refresh testimonials list
      const testimonialsResponse = await axios.get(
        `${API_URL}/companies/${company.id}/testimonials?organization_id=${selectedOrg.id}`,
        { headers: getAuthHeaders() },
      );
      setTestimonials(testimonialsResponse.data);
      setEditingTestimonial(null);
    } catch (error) {
      // console.error removed
      alert('Failed to update testimonial. Please try again.');
    }
  };

  const handleDeleteTestimonial = async (testimonialId: string) => {
    if (
      !selectedOrg ||
      !window.confirm('Are you sure you want to delete this testimonial?')
    )
      return;

    try {
      await axios.delete(
        `${API_URL}/companies/testimonials/${testimonialId}?organization_id=${selectedOrg.id}`,
        { headers: getAuthHeaders() },
      );
      setTestimonials(testimonials.filter((t) => t.id !== testimonialId));
    } catch (error) {
      // console.error removed
      alert('Failed to delete testimonial. Please try again.');
    }
  };

  const handleUpdateCaseStudy = async (data: any) => {
    if (!editingCaseStudy || !selectedOrg) return;

    // Clean up data - convert empty strings to null for optional fields
    const cleanData = {
      ...data,
      proposition_id: data.proposition_id || null,
      description: data.description || null,
      published_date: data.published_date || null,
    };
    try {
      await axios.put(
        `${API_URL}/companies/case-studies/${editingCaseStudy.id}?organization_id=${selectedOrg.id}`,
        cleanData,
        { headers: getAuthHeaders() },
      );
      // Refresh case studies list
      const caseStudiesResponse = await axios.get(
        `${API_URL}/companies/${company.id}/case-studies?organization_id=${selectedOrg.id}`,
        { headers: getAuthHeaders() },
      );
      setCaseStudies(caseStudiesResponse.data);
      setEditingCaseStudy(null);
    } catch (error) {
      // console.error removed
      alert('Failed to update case study. Please try again.');
    }
  };

  const handleDeleteCaseStudy = async (caseStudyId: string) => {
    if (
      !selectedOrg ||
      !window.confirm('Are you sure you want to delete this case study?')
    )
      return;

    try {
      await axios.delete(
        `${API_URL}/companies/case-studies/${caseStudyId}?organization_id=${selectedOrg.id}`,
        { headers: getAuthHeaders() },
      );
      setCaseStudies(caseStudies.filter((cs) => cs.id !== caseStudyId));
    } catch (error) {
      // console.error removed
      alert('Failed to delete case study. Please try again.');
    }
  };

  const handleUpdateAward = async (data: any) => {
    if (!editingAward || !selectedOrg) return;

    // Clean up data - convert empty strings to null for optional fields
    const cleanData = {
      ...data,
      proposition_id: data.proposition_id || null,
      awarding_body: data.awarding_body || null,
      date_won: data.date_won || null,
      description: data.description || null,
    };
    try {
      await axios.put(
        `${API_URL}/companies/awards/${editingAward.id}?organization_id=${selectedOrg.id}`,
        cleanData,
        { headers: getAuthHeaders() },
      );
      // Refresh awards list
      const awardsResponse = await axios.get(
        `${API_URL}/companies/${company.id}/awards?organization_id=${selectedOrg.id}`,
        { headers: getAuthHeaders() },
      );
      setAwards(awardsResponse.data);
      setEditingAward(null);
    } catch (error: any) {
      // console.error removed
      // console.error removed
      alert(
        `Failed to update award: ${error.response?.data?.detail || error.message || 'Unknown error'}`,
      );
    }
  };

  const handleDeleteAward = async (awardId: string) => {
    if (
      !selectedOrg ||
      !window.confirm('Are you sure you want to delete this award?')
    )
      return;

    try {
      await axios.delete(
        `${API_URL}/companies/awards/${awardId}?organization_id=${selectedOrg.id}`,
        { headers: getAuthHeaders() },
      );
      setAwards(awards.filter((a) => a.id !== awardId));
    } catch (error) {
      // console.error removed
      alert('Failed to delete award. Please try again.');
    }
  };

  const handleAddOpportunity = async (data: any) => {
    if (!company || !selectedOrg) return;

    try {
      await axios.post(
        `${API_URL}/opportunities?organization_id=${selectedOrg.id}`,
        {
          ...data,
          company_id: company.id,
        },
        { headers: getAuthHeaders() },
      );
      // Refresh opportunities list
      const oppsResponse = await axios.get(
        `${API_URL}/companies/${company.id}/opportunities?organization_id=${selectedOrg.id}`,
        { headers: getAuthHeaders() },
      );
      setOpportunities(oppsResponse.data);
      // Re-fetch company data to get updated financial metrics
      const companyResponse = await axios.get(
        `${API_URL}/companies/${company.id}?organization_id=${selectedOrg.id}`,
        { headers: getAuthHeaders() },
      );
      setCompany(companyResponse.data);
    } catch (error) {
      // console.error removed
      alert('Failed to add opportunity. Please try again.');
    }
  };

  const handleDeleteOpportunity = async (opportunityId: string) => {
    if (
      !selectedOrg ||
      !window.confirm('Are you sure you want to delete this opportunity?')
    )
      return;

    try {
      await axios.delete(
        `${API_URL}/opportunities/${opportunityId}?organization_id=${selectedOrg.id}`,
        { headers: getAuthHeaders() },
      );
      setOpportunities(opportunities.filter((opp) => opp.id !== opportunityId));
      // Re-fetch company data to get updated financial metrics
      const companyResponse = await axios.get(
        `${API_URL}/companies/${company.id}?organization_id=${selectedOrg.id}`,
        { headers: getAuthHeaders() },
      );
      setCompany(companyResponse.data);
    } catch (error) {
      // console.error removed
    }
  };

  const handleUpdateOpportunity = async (data: any) => {
    if (!editingOpportunity || !selectedOrg) return;

    try {
      await axios.put(
        `${API_URL}/opportunities/${editingOpportunity.id}?organization_id=${selectedOrg.id}`,
        {
          ...data,
          company_id: company.id,
        },
        { headers: getAuthHeaders() },
      );
      // Refresh opportunities list
      const oppsResponse = await axios.get(
        `${API_URL}/companies/${company.id}/opportunities?organization_id=${selectedOrg.id}`,
        { headers: getAuthHeaders() },
      );
      setOpportunities(oppsResponse.data);
      setEditingOpportunity(null);
      // Re-fetch company data to get updated financial metrics
      const companyResponse = await axios.get(
        `${API_URL}/companies/${company.id}?organization_id=${selectedOrg.id}`,
        { headers: getAuthHeaders() },
      );
      setCompany(companyResponse.data);
    } catch (error) {
      // console.error removed
      alert('Failed to update opportunity. Please try again.');
    }
  };

  const handleSaveContact = async () => {
    if (!company || !selectedOrg) return;

    try {
      // Prepare data: convert empty strings to null for UUID fields
      const contactData = {
        ...contactForm,
        reports_to_contact_id: contactForm.reports_to_contact_id || null,
      };

      if (editingContact) {
        // Update existing
        const response = await axios.put(
          `${API_URL}/contacts/${editingContact.id}?organization_id=${selectedOrg.id}`,
          contactData,
          { headers: getAuthHeaders() },
        );
        setContacts(
          contacts.map((c) => (c.id === editingContact.id ? response.data : c)),
        );
      } else {
        // Create new
        const response = await axios.post(
          `${API_URL}/companies/${company.id}/contacts?organization_id=${selectedOrg.id}`,
          contactData,
          { headers: getAuthHeaders() },
        );
        setContacts([...contacts, response.data]);
      }

      // Reset form
      setContactForm({
        first_name: '',
        last_name: '',
        title: '',
        email: '',
        phone: '',
        department: '',
        role_type: '',
        influence_level: '',
        relationship_strength: 0,
        reports_to_contact_id: '',
        hierarchy_level: 0,
        notes: '',
      });
      setEditingContact(null);
      setShowContactForm(false);
    } catch (error) {
      // console.error removed
      alert('Failed to save contact. Please try again.');
    }
  };

  const handleSaveTestimonial = async () => {
    if (!company || !selectedOrg) return;

    try {
      if (editingTestimonial) {
        // Update existing
        await handleUpdateTestimonial(testimonialForm);
      } else {
        // Create new
        await handleAddTestimonial(testimonialForm);
      }

      // Reset form
      setTestimonialForm({
        testimonial_text: '',
        author_name: '',
        author_title: '',
        proposition_id: '',
      });
      setEditingTestimonial(null);
      setShowTestimonialForm(false);
    } catch (error) {
      // console.error removed
      alert('Failed to save testimonial. Please try again.');
    }
  };

  const handleSaveCaseStudy = async () => {
    if (!company || !selectedOrg) return;

    try {
      if (editingCaseStudy) {
        // Update existing
        await handleUpdateCaseStudy(caseStudyForm);
      } else {
        // Create new
        await handleAddCaseStudy(caseStudyForm);
      }

      // Reset form
      setCaseStudyForm({
        title: '',
        description: '',
        published_date: '',
        proposition_id: '',
      });
      setEditingCaseStudy(null);
      setShowCaseStudyForm(false);
    } catch (error) {
      // console.error removed
      alert('Failed to save case study. Please try again.');
    }
  };

  const handleSaveAward = async () => {
    if (!company || !selectedOrg) return;

    try {
      if (editingAward) {
        // Update existing
        await handleUpdateAward(awardForm);
      } else {
        // Create new
        await handleAddAward(awardForm);
      }

      // Reset form
      setAwardForm({
        award_name: '',
        awarding_body: '',
        date_won: '',
        description: '',
        proposition_id: '',
      });
      setEditingAward(null);
      setShowAwardForm(false);
    } catch (error) {
      // console.error removed
      alert('Failed to save award. Please try again.');
    }
  };

  const handleDeleteContact = async (contactId: string) => {
    if (
      !selectedOrg ||
      !window.confirm('Are you sure you want to delete this contact?')
    )
      return;

    try {
      await axios.delete(
        `${API_URL}/contacts/${contactId}?organization_id=${selectedOrg.id}`,
        { headers: getAuthHeaders() },
      );
      setContacts(contacts.filter((contact) => contact.id !== contactId));
    } catch (error) {
      // console.error removed
    }
  };

  const formatCurrencyValue = (value?: number) => {
    const currencyCode = organization?.currency || 'USD';
    return formatCurrency(value, currencyCode, {
      compact: true,
      maximumFractionDigits: 1,
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'suspect':
        return 'bg-purple-100 text-purple-700';
      case 'target':
        return 'bg-gray-100 text-gray-700';
      case 'prospect':
        return 'bg-blue-100 text-blue-700';
      case 'customer':
        return 'bg-green-100 text-green-700';
      case 'former_customer':
        return 'bg-yellow-100 text-yellow-700';
      case 'lost':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Task handlers
  const handleTaskUpdate = async (
    taskId: string,
    updates: Partial<MasterPlanTask>,
  ) => {
    if (!selectedOrg) return;
    try {
      const updatedTask = await masterPlanTaskService.update(
        selectedOrg.id,
        taskId,
        updates,
      );
      setTasks(tasks.map((t) => (t.id === taskId ? updatedTask : t)));
    } catch (error) {
      // Error updating task
    }
  };

  const handleTaskCreate = async (taskData: MasterPlanTask) => {
    // TaskModal already creates the task, just add it to the list
    setTasks([...tasks, taskData]);
    return taskData;
  };

  const handleTaskDelete = async (taskId: string) => {
    if (!selectedOrg) return;
    try {
      await masterPlanTaskService.delete(selectedOrg.id, taskId);
      setTasks(tasks.filter((t) => t.id !== taskId));
    } catch (error) {
      // Error deleting task
    }
  };

  if (!company) {
    return (
      <div className="p-6">
        <p className="text-gray-600">Loading company details...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/companies')}
          className="mr-4 p-2 hover:bg-gray-100 rounded-full"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <h1 className="text-3xl font-bold flex-grow">{company.name}</h1>
        {canEdit && (
          <div className="flex gap-2">
            {editingCompany ? (
              <>
                <button
                  onClick={() => setEditingCompany(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCompany}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Save
                </button>
              </>
            ) : (
              <button
                onClick={() => setEditingCompany(true)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Edit Company
              </button>
            )}
          </div>
        )}
      </div>
      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => handleTabChange('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => handleTabChange('contacts')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'contacts'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Contacts
          </button>
          <button
            onClick={() => handleTabChange('planning')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'planning'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Planning
          </button>
          <button
            onClick={() => handleTabChange('tasks')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'tasks'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Tasks
          </button>
          <button
            onClick={() => handleTabChange('research')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'research'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Research
          </button>
        </nav>
      </div>
      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Company Info */}
          <div className="lg:col-span-1 space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">
                Company Information
              </h3>
              {editingCompany ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      value={companyForm.name}
                      onChange={(e) =>
                        setCompanyForm({ ...companyForm, name: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={companyForm.status}
                      onChange={(e) =>
                        setCompanyForm({
                          ...companyForm,
                          status: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {COMPANY_STATUSES.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Strategic Owner
                    </label>
                    <select
                      value={companyForm.strategic_owner_id}
                      onChange={(e) =>
                        setCompanyForm({
                          ...companyForm,
                          strategic_owner_id: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Unassigned</option>
                      {organizationMembers.map((member) => (
                        <option key={member.user_id} value={member.user_id}>
                          {member.user_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={companyForm.description}
                      onChange={(e) =>
                        setCompanyForm({
                          ...companyForm,
                          description: e.target.value,
                        })
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Website
                    </label>
                    <input
                      type="url"
                      value={companyForm.website}
                      onChange={(e) =>
                        setCompanyForm({
                          ...companyForm,
                          website: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      LinkedIn
                    </label>
                    <input
                      type="url"
                      value={companyForm.linkedin_url}
                      onChange={(e) =>
                        setCompanyForm({
                          ...companyForm,
                          linkedin_url: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Employee Count
                    </label>
                    <input
                      type="text"
                      value={companyForm.employee_count}
                      onChange={(e) =>
                        setCompanyForm({
                          ...companyForm,
                          employee_count: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Annual Revenue
                    </label>
                    <input
                      type="number"
                      value={companyForm.annual_revenue}
                      onChange={(e) =>
                        setCompanyForm({
                          ...companyForm,
                          annual_revenue: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Segments
                    </label>
                    <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2">
                      {segments.map((segment) => (
                        <label key={segment.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={companyForm.segment_ids.includes(
                              segment.id,
                            )}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setCompanyForm({
                                  ...companyForm,
                                  segment_ids: [
                                    ...companyForm.segment_ids,
                                    segment.id,
                                  ],
                                });
                              } else {
                                setCompanyForm({
                                  ...companyForm,
                                  segment_ids: companyForm.segment_ids.filter(
                                    (id) => id !== segment.id,
                                  ),
                                });
                              }
                            }}
                            className="mr-2"
                          />
                          <span className="text-sm">{segment.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Status:</span>
                    <span
                      className={`px-3 py-1 rounded-full text-sm ${getStatusColor(company.status || 'target')}`}
                    >
                      {formatCompanyStatus(company.status)}
                    </span>
                  </div>
                  {company.strategic_owner_name && (
                    <p>
                      <span className="font-medium">Strategic Owner:</span>{' '}
                      {company.strategic_owner_name}
                    </p>
                  )}
                  {company.description && (
                    <p>
                      <span className="font-medium">Description:</span>{' '}
                      {company.description}
                    </p>
                  )}
                  {company.website && (
                    <p>
                      <span className="font-medium">Website:</span>{' '}
                      <a
                        href={
                          company.website.startsWith('http')
                            ? company.website
                            : `https://${company.website}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        {company.website}
                      </a>
                    </p>
                  )}
                  {company.linkedin_url && (
                    <p>
                      <span className="font-medium">LinkedIn:</span>{' '}
                      <a
                        href={
                          company.linkedin_url.startsWith('http')
                            ? company.linkedin_url
                            : `https://${company.linkedin_url}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        {company.linkedin_url}
                      </a>
                    </p>
                  )}
                  {company.employee_count && (
                    <p>
                      <span className="font-medium">Employees:</span>{' '}
                      {company.employee_count}
                    </p>
                  )}
                  {company.annual_revenue && (
                    <p>
                      <span className="font-medium">Annual Revenue:</span>{' '}
                      {formatCurrencyValue(company.annual_revenue)}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Segments */}
            {company.segments && company.segments.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Segments</h3>
                <div className="flex flex-wrap gap-2">
                  {company.segments.map((segment: any) => (
                    <span
                      key={segment.id}
                      className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                    >
                      {segment.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Financial Summary */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Financial Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Booked Business:</span>
                  <span className="font-semibold text-green-600">
                    {formatCurrencyValue(company.booked_business || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Open Pipeline:</span>
                  <span className="font-semibold text-blue-600">
                    {formatCurrencyValue(company.open_pipeline_unweighted || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Weighted Pipeline:</span>
                  <span className="font-semibold text-yellow-600">
                    {formatCurrencyValue(company.open_pipeline_weighted || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Lost Business:</span>
                  <span className="font-semibold text-red-600">
                    {formatCurrencyValue(company.lost_business || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Contacts, Opportunities, and Business Metrics */}
          <div className="lg:col-span-2 space-y-6">
            {/* Opportunities */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Opportunities</h3>
                {canEdit && (
                  <button
                    onClick={() => setShowOpportunityForm(true)}
                    className="text-sm px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Add Opportunity
                  </button>
                )}
              </div>
              {opportunities.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3">Name</th>
                        <th className="text-left py-2 px-3">Proposition</th>
                        <th className="text-left py-2 px-3">Value</th>
                        <th className="text-left py-2 px-3">Status</th>
                        <th className="text-left py-2 px-3">Probability</th>
                        {(canEdit || canDelete) && (
                          <th className="text-right py-2 px-3">Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {opportunities.map((opp) => {
                        // console.log removed
                        return (
                          <tr
                            key={opp.id}
                            className="border-b hover:bg-gray-50"
                          >
                            <td className="py-2 px-3">
                              {opp.name || 'Unnamed'}
                            </td>
                            <td className="py-2 px-3">
                              {opp.proposition_name || '-'}
                            </td>
                            <td className="py-2 px-3">
                              {formatCurrencyValue(opp.pipeline_value)}
                            </td>
                            <td className="py-2 px-3">
                              <span
                                className={`px-2 py-1 rounded text-xs ${
                                  opp.status?.toLowerCase() === 'won'
                                    ? 'bg-green-100 text-green-700'
                                    : opp.status?.toLowerCase() === 'lost'
                                      ? 'bg-red-100 text-red-700'
                                      : 'bg-blue-100 text-blue-700'
                                }`}
                              >
                                {formatOpportunityStatus(opp.status)}
                              </span>
                            </td>
                            <td className="py-2 px-3">
                              {opp.probability || 0}%
                            </td>
                            {(canEdit || canDelete) && (
                              <td className="py-2 px-3 text-right">
                                <div className="flex justify-end gap-2">
                                  {canEdit && (
                                    <button
                                      onClick={() => setEditingOpportunity(opp)}
                                      className="text-blue-600 hover:text-blue-900"
                                    >
                                      <PencilIcon className="h-4 w-4" />
                                    </button>
                                  )}
                                  {canDelete && (
                                    <button
                                      onClick={() =>
                                        handleDeleteOpportunity(opp.id)
                                      }
                                      className="text-red-600 hover:text-red-900"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500">No opportunities yet</p>
              )}

              {canEdit && showOpportunityForm && (
                <ItemForm
                  label="Opportunity"
                  onSave={(data) => {
                    handleAddOpportunity(data);
                    setShowOpportunityForm(false);
                  }}
                  onCancel={() => setShowOpportunityForm(false)}
                  fields={[
                    { name: 'name', label: 'Opportunity Name', required: true },
                    {
                      name: 'proposition_id',
                      label: 'Proposition',
                      type: 'select',
                      required: true,
                      options: propositions.map((p) => ({
                        value: p.id,
                        label: p.name,
                      })),
                    },
                    {
                      name: 'pipeline_value',
                      label: 'Pipeline Value',
                      type: 'number',
                    },
                    {
                      name: 'status',
                      label: 'Status',
                      type: 'select',
                      options: [...OPPORTUNITY_STATUSES],
                    },
                    {
                      name: 'probability',
                      label: 'Probability (%)',
                      type: 'number',
                    },
                    { name: 'notes', label: 'Notes', type: 'textarea' },
                  ]}
                />
              )}

              {canEdit && editingOpportunity && (
                <ItemForm
                  label="Opportunity"
                  onSave={handleUpdateOpportunity}
                  onCancel={() => setEditingOpportunity(null)}
                  fields={[
                    { name: 'name', label: 'Opportunity Name', required: true },
                    {
                      name: 'proposition_id',
                      label: 'Proposition',
                      type: 'select',
                      required: true,
                      options: propositions.map((p) => ({
                        value: p.id,
                        label: p.name,
                      })),
                    },
                    {
                      name: 'pipeline_value',
                      label: 'Pipeline Value',
                      type: 'number',
                    },
                    {
                      name: 'status',
                      label: 'Status',
                      type: 'select',
                      options: [...OPPORTUNITY_STATUSES],
                    },
                    {
                      name: 'probability',
                      label: 'Probability (%)',
                      type: 'number',
                    },
                    { name: 'notes', label: 'Notes', type: 'textarea' },
                  ]}
                  initialData={{
                    name: editingOpportunity.name || '',
                    proposition_id: editingOpportunity.proposition_id || '',
                    pipeline_value: editingOpportunity.pipeline_value || '',
                    status: editingOpportunity.status || 'Open',
                    probability: editingOpportunity.probability || '',
                    notes: editingOpportunity.notes || '',
                  }}
                  isEdit={true}
                />
              )}
            </div>

            {/* Testimonials */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Testimonials</h3>
                {canEdit && (
                  <button
                    onClick={() => setShowTestimonialForm(true)}
                    className="text-sm px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Add Testimonial
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {testimonials.map((testimonial) => (
                  <EditableListItem
                    key={testimonial.id}
                    canEdit={canEdit}
                    onEdit={() => {
                      setEditingTestimonial(testimonial);
                      setTestimonialForm({
                        testimonial_text: testimonial.testimonial_text || '',
                        author_name: testimonial.author_name || '',
                        author_title: testimonial.author_title || '',
                        proposition_id: testimonial.proposition_id || '',
                      });
                      setShowTestimonialForm(true);
                    }}
                    onDelete={() => handleDeleteTestimonial(testimonial.id)}
                    className="border-l-4 border-blue-500 pl-4 py-2"
                  >
                    <p className="text-gray-700 italic">{`"${testimonial.testimonial_text}"`}</p>
                    <div className="mt-2 text-sm text-gray-600">
                      <span className="font-medium">
                        {testimonial.author_name}
                      </span>
                      {testimonial.author_title && (
                        <span> - {testimonial.author_title}</span>
                      )}
                      {testimonial.proposition_name && (
                        <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                          {testimonial.proposition_name}
                        </span>
                      )}
                    </div>
                  </EditableListItem>
                ))}
              </div>
            </div>

            {/* Case Studies */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Case Studies</h3>
                {canEdit && (
                  <button
                    onClick={() => setShowCaseStudyForm(true)}
                    className="text-sm px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Add Case Study
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {caseStudies.map((caseStudy) => (
                  <EditableListItem
                    key={caseStudy.id}
                    canEdit={canEdit}
                    onEdit={() => {
                      setEditingCaseStudy(caseStudy);
                      setCaseStudyForm({
                        title: caseStudy.title || '',
                        description: caseStudy.description || '',
                        published_date: caseStudy.published_date
                          ? new Date(caseStudy.published_date)
                              .toISOString()
                              .split('T')[0]
                          : '',
                        proposition_id: caseStudy.proposition_id || '',
                      });
                      setShowCaseStudyForm(true);
                    }}
                    onDelete={() => handleDeleteCaseStudy(caseStudy.id)}
                  >
                    <h4 className="font-semibold">{caseStudy.title}</h4>
                    {caseStudy.description && (
                      <p className="text-gray-700 mt-1">
                        {caseStudy.description}
                      </p>
                    )}
                    {caseStudy.published_date && (
                      <p className="text-sm text-gray-600 mt-2">
                        Published:{' '}
                        {new Date(
                          caseStudy.published_date,
                        ).toLocaleDateString()}
                      </p>
                    )}
                    {caseStudy.proposition_name && (
                      <span className="inline-block mt-2 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                        {caseStudy.proposition_name}
                      </span>
                    )}
                  </EditableListItem>
                ))}
              </div>
            </div>

            {/* Awards */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Awards</h3>
                {canEdit && (
                  <button
                    onClick={() => setShowAwardForm(true)}
                    className="text-sm px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Add Award
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {awards.map((award) => (
                  <EditableListItem
                    key={award.id}
                    canEdit={canEdit}
                    onEdit={() => {
                      setEditingAward(award);
                      setAwardForm({
                        award_name: award.award_name || '',
                        awarding_body: award.awarding_body || '',
                        date_won: award.date_won
                          ? new Date(award.date_won).toISOString().split('T')[0]
                          : '',
                        description: award.description || '',
                        proposition_id: award.proposition_id || '',
                      });
                      setShowAwardForm(true);
                    }}
                    onDelete={() => handleDeleteAward(award.id)}
                    noPadding={true}
                    className="p-4"
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">🏆</span>
                      <div className="flex-1">
                        <h4 className="font-semibold">{award.award_name}</h4>
                        {award.awarding_body && (
                          <p className="text-sm text-gray-600">
                            {award.awarding_body}
                          </p>
                        )}
                        {award.date_won && (
                          <p className="text-sm text-gray-600">
                            {new Date(award.date_won).toLocaleDateString()}
                          </p>
                        )}
                        {award.proposition_name && (
                          <span className="inline-block mt-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                            {award.proposition_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </EditableListItem>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}{' '}
      {/* Close overview tab */}
      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-gray-500/75"
              onClick={() => setShowContactForm(false)}
            ></div>

            <div className="relative bg-white rounded-lg max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-semibold mb-4">
                {editingContact ? 'Edit Contact' : 'Add Contact'}
              </h2>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSaveContact();
                }}
                className="space-y-6"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={contactForm.first_name}
                      onChange={(e) =>
                        setContactForm({
                          ...contactForm,
                          first_name: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={contactForm.last_name}
                      onChange={(e) =>
                        setContactForm({
                          ...contactForm,
                          last_name: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    value={contactForm.title}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, title: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, email: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={contactForm.phone}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, phone: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Stakeholder Information Section */}
                <div className="border-t pt-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">
                    Stakeholder Information
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Department
                      </label>
                      <input
                        type="text"
                        value={contactForm.department}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            department: e.target.value,
                          })
                        }
                        placeholder="e.g. Engineering, Sales"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Role Type
                      </label>
                      <select
                        value={contactForm.role_type}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            role_type: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select role...</option>
                        <option value="decision_maker">Decision Maker</option>
                        <option value="influencer">Influencer</option>
                        <option value="champion">Champion</option>
                        <option value="blocker">Blocker</option>
                        <option value="end_user">End User</option>
                        <option value="technical_evaluator">
                          Technical Evaluator
                        </option>
                        <option value="budget_holder">Budget Holder</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Influence Level
                      </label>
                      <select
                        value={contactForm.influence_level}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            influence_level: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select influence...</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Relationship Strength (0-100)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={contactForm.relationship_strength}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            relationship_strength:
                              parseInt(e.target.value) || 0,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Organizational Hierarchy Section */}
                <div className="border-t pt-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">
                    Organizational Hierarchy
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reports To
                      </label>
                      <select
                        value={contactForm.reports_to_contact_id}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            reports_to_contact_id: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">None (top-level)</option>
                        {contacts
                          .filter(
                            (c) =>
                              !editingContact || c.id !== editingContact.id,
                          )
                          .sort((a, b) =>
                            a.last_name.localeCompare(b.last_name),
                          )
                          .map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.first_name} {c.last_name}
                              {c.title ? ` - ${c.title}` : ''}
                            </option>
                          ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Select this person&apos;s manager
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Hierarchy Level
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        value={contactForm.hierarchy_level}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            hierarchy_level: parseInt(e.target.value) || 0,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        0 = CEO, 1 = direct report, etc.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={contactForm.notes}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, notes: e.target.value })
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowContactForm(false);
                      setEditingContact(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {editingContact ? 'Save Changes' : 'Add Contact'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {/* Testimonial Modal */}
      {showTestimonialForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-gray-500/75"
              onClick={() => {
                setShowTestimonialForm(false);
                setEditingTestimonial(null);
              }}
            ></div>

            <div className="relative bg-white rounded-lg max-w-md w-full p-6">
              <h2 className="text-xl font-semibold mb-4">
                {editingTestimonial ? 'Edit Testimonial' : 'Add Testimonial'}
              </h2>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSaveTestimonial();
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Testimonial *
                  </label>
                  <textarea
                    value={testimonialForm.testimonial_text}
                    onChange={(e) =>
                      setTestimonialForm({
                        ...testimonialForm,
                        testimonial_text: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={4}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Author Name *
                  </label>
                  <input
                    type="text"
                    value={testimonialForm.author_name}
                    onChange={(e) =>
                      setTestimonialForm({
                        ...testimonialForm,
                        author_name: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Author Title
                  </label>
                  <input
                    type="text"
                    value={testimonialForm.author_title}
                    onChange={(e) =>
                      setTestimonialForm({
                        ...testimonialForm,
                        author_title: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Related Proposition
                  </label>
                  <select
                    value={testimonialForm.proposition_id}
                    onChange={(e) =>
                      setTestimonialForm({
                        ...testimonialForm,
                        proposition_id: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select...</option>
                    {propositions.map((prop) => (
                      <option key={prop.id} value={prop.id}>
                        {prop.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowTestimonialForm(false);
                      setEditingTestimonial(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {editingTestimonial ? 'Save Changes' : 'Add Testimonial'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {/* Case Study Modal */}
      {showCaseStudyForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-gray-500/75"
              onClick={() => {
                setShowCaseStudyForm(false);
                setEditingCaseStudy(null);
              }}
            ></div>

            <div className="relative bg-white rounded-lg max-w-md w-full p-6">
              <h2 className="text-xl font-semibold mb-4">
                {editingCaseStudy ? 'Edit Case Study' : 'Add Case Study'}
              </h2>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSaveCaseStudy();
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={caseStudyForm.title}
                    onChange={(e) =>
                      setCaseStudyForm({
                        ...caseStudyForm,
                        title: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={caseStudyForm.description}
                    onChange={(e) =>
                      setCaseStudyForm({
                        ...caseStudyForm,
                        description: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Published Date
                  </label>
                  <input
                    type="date"
                    value={caseStudyForm.published_date}
                    onChange={(e) =>
                      setCaseStudyForm({
                        ...caseStudyForm,
                        published_date: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Related Proposition
                  </label>
                  <select
                    value={caseStudyForm.proposition_id}
                    onChange={(e) =>
                      setCaseStudyForm({
                        ...caseStudyForm,
                        proposition_id: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select...</option>
                    {propositions.map((prop) => (
                      <option key={prop.id} value={prop.id}>
                        {prop.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCaseStudyForm(false);
                      setEditingCaseStudy(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {editingCaseStudy ? 'Save Changes' : 'Add Case Study'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {/* Award Modal */}
      {showAwardForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-gray-500/75"
              onClick={() => {
                setShowAwardForm(false);
                setEditingAward(null);
              }}
            ></div>

            <div className="relative bg-white rounded-lg max-w-md w-full p-6">
              <h2 className="text-xl font-semibold mb-4">
                {editingAward ? 'Edit Award' : 'Add Award'}
              </h2>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSaveAward();
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Award Name *
                  </label>
                  <input
                    type="text"
                    value={awardForm.award_name}
                    onChange={(e) =>
                      setAwardForm({ ...awardForm, award_name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Awarding Body
                  </label>
                  <input
                    type="text"
                    value={awardForm.awarding_body}
                    onChange={(e) =>
                      setAwardForm({
                        ...awardForm,
                        awarding_body: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date Won
                  </label>
                  <input
                    type="date"
                    value={awardForm.date_won}
                    onChange={(e) =>
                      setAwardForm({ ...awardForm, date_won: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={awardForm.description}
                    onChange={(e) =>
                      setAwardForm({
                        ...awardForm,
                        description: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Related Proposition
                  </label>
                  <select
                    value={awardForm.proposition_id}
                    onChange={(e) =>
                      setAwardForm({
                        ...awardForm,
                        proposition_id: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select...</option>
                    {propositions.map((prop) => (
                      <option key={prop.id} value={prop.id}>
                        {prop.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAwardForm(false);
                      setEditingAward(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {editingAward ? 'Save Changes' : 'Add Award'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {/* Tasks Tab */}
      {activeTab === 'tasks' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Tasks</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setTaskViewMode('kanban')}
                  className={`px-3 py-1 rounded ${
                    taskViewMode === 'kanban'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  Kanban
                </button>
                <button
                  onClick={() => setTaskViewMode('list')}
                  className={`px-3 py-1 rounded ${
                    taskViewMode === 'list'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  List
                </button>
              </div>
            </div>
          </div>
          <div className="p-4">
            {taskViewMode === 'kanban' ? (
              <TaskKanbanView
                tasks={tasks}
                entityType="company"
                entityId={id}
                canEdit={canEdit}
                organizationId={selectedOrg?.id || ''}
                segments={segments}
                propositions={propositions}
                groups={[]}
                companies={[company]}
                positions={[]}
                onTaskUpdate={handleTaskUpdate}
                onTaskCreate={handleTaskCreate}
                onTaskDelete={handleTaskDelete}
              />
            ) : (
              <TaskListView
                tasks={tasks}
                entityType="company"
                entityId={id}
                canEdit={canEdit}
                organizationId={selectedOrg?.id || ''}
                segments={segments}
                propositions={propositions}
                groups={[]}
                companies={[company]}
                positions={[]}
                onTaskUpdate={handleTaskUpdate}
                onTaskCreate={handleTaskCreate}
                onTaskDelete={handleTaskDelete}
              />
            )}
          </div>
        </div>
      )}
      {/* Research Tab */}
      {activeTab === 'research' && company && selectedOrg && (
        <ResearchTab
          organizationId={selectedOrg.id}
          entityType={ResearchEntityType.COMPANY}
          entityId={company.id}
          entityName={company.name}
          canEdit={canEdit}
        />
      )}
      {/* Contacts Tab */}
      {activeTab === 'contacts' && (
        <div className="space-y-6">
          {/* Header with view switcher and actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <h2 className="text-xl font-semibold">Contacts</h2>
              <div className="flex items-center gap-4">
                {/* View Switcher */}
                <div className="flex border border-gray-300 rounded-md overflow-hidden">
                  <button
                    onClick={() => setContactsView('list')}
                    className={`px-4 py-2 text-sm font-medium ${
                      contactsView === 'list'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    List
                  </button>
                  <button
                    onClick={() => setContactsView('stakeholder')}
                    className={`px-4 py-2 text-sm font-medium border-l border-gray-300 ${
                      contactsView === 'stakeholder'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Stakeholder Map
                  </button>
                  <button
                    onClick={() => setContactsView('orgchart')}
                    className={`px-4 py-2 text-sm font-medium border-l border-gray-300 ${
                      contactsView === 'orgchart'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Org Chart
                  </button>
                </div>
                {canEdit && (
                  <button
                    onClick={() => {
                      setEditingContact(null);
                      setContactForm({
                        first_name: '',
                        last_name: '',
                        title: '',
                        email: '',
                        phone: '',
                        department: '',
                        role_type: '',
                        influence_level: '',
                        relationship_strength: 0,
                        reports_to_contact_id: '',
                        hierarchy_level: 0,
                        notes: '',
                      });
                      setShowContactForm(true);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Add Contact
                  </button>
                )}
              </div>
            </div>

            {/* List View - Search and Sort Controls */}
            {contactsView === 'list' && (
              <>
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="Search contacts by name or email..."
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={contactSortBy}
                      onChange={(e) => setContactSortBy(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="last_name">Sort by Last Name</option>
                      <option value="first_name">Sort by First Name</option>
                      <option value="email">Sort by Email</option>
                      <option value="influence_level">Sort by Influence</option>
                      <option value="relationship_strength">
                        Sort by Relationship
                      </option>
                    </select>
                    <button
                      onClick={() =>
                        setContactSortOrder(
                          contactSortOrder === 'asc' ? 'desc' : 'asc',
                        )
                      }
                      className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                      title={
                        contactSortOrder === 'asc' ? 'Ascending' : 'Descending'
                      }
                    >
                      {contactSortOrder === 'asc' ? '↑' : '↓'}
                    </button>
                  </div>
                </div>

                {/* Contacts List */}
                {contacts.length > 0 ? (
                  <div className="space-y-4">
                    {contacts
                      .filter((contact) => {
                        if (!contactSearch) return true;
                        const searchLower = contactSearch.toLowerCase();
                        return (
                          contact.first_name
                            ?.toLowerCase()
                            .includes(searchLower) ||
                          contact.last_name
                            ?.toLowerCase()
                            .includes(searchLower) ||
                          contact.email?.toLowerCase().includes(searchLower)
                        );
                      })
                      .sort((a, b) => {
                        const aVal = a[contactSortBy] || '';
                        const bVal = b[contactSortBy] || '';
                        const comparison =
                          aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
                        return contactSortOrder === 'asc'
                          ? comparison
                          : -comparison;
                      })
                      .map((contact) => (
                        <div
                          key={contact.id}
                          className="border rounded-lg p-6 hover:shadow-md transition-shadow relative group"
                        >
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Left Column - Basic Info */}
                            <div>
                              <div className="font-semibold text-lg mb-1">
                                {contact.first_name} {contact.last_name}
                              </div>
                              {contact.title && (
                                <div className="text-gray-600 mb-2">
                                  {contact.title}
                                </div>
                              )}
                              {contact.department && (
                                <div className="text-sm text-gray-500 mb-2">
                                  <span className="font-medium">
                                    Department:
                                  </span>{' '}
                                  {contact.department}
                                </div>
                              )}
                              <div className="space-y-1 text-sm">
                                {contact.email && (
                                  <div>
                                    <a
                                      href={`mailto:${contact.email}`}
                                      className="text-blue-600 hover:underline"
                                    >
                                      {contact.email}
                                    </a>
                                  </div>
                                )}
                                {contact.phone && (
                                  <div className="text-gray-600">
                                    {contact.phone}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Middle Column - Stakeholder Info */}
                            <div className="space-y-3">
                              {contact.role_type && (
                                <div>
                                  <span className="text-xs font-medium text-gray-500 uppercase">
                                    Role Type
                                  </span>
                                  <div className="text-sm capitalize">
                                    {contact.role_type.replace(/_/g, ' ')}
                                  </div>
                                </div>
                              )}
                              {contact.influence_level && (
                                <div>
                                  <span className="text-xs font-medium text-gray-500 uppercase">
                                    Influence Level
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <span
                                      className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                        contact.influence_level === 'high'
                                          ? 'bg-red-100 text-red-800'
                                          : contact.influence_level === 'medium'
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : 'bg-green-100 text-green-800'
                                      }`}
                                    >
                                      {contact.influence_level}
                                    </span>
                                  </div>
                                </div>
                              )}
                              {contact.relationship_strength !== null &&
                                contact.relationship_strength !== undefined && (
                                  <div>
                                    <span className="text-xs font-medium text-gray-500 uppercase">
                                      Relationship Strength
                                    </span>
                                    <div className="flex items-center gap-2">
                                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                                        <div
                                          className={`h-2 rounded-full ${
                                            contact.relationship_strength >= 70
                                              ? 'bg-green-500'
                                              : contact.relationship_strength >=
                                                  40
                                                ? 'bg-yellow-500'
                                                : 'bg-red-500'
                                          }`}
                                          style={{
                                            width: `${contact.relationship_strength}%`,
                                          }}
                                        ></div>
                                      </div>
                                      <span className="text-sm font-medium">
                                        {contact.relationship_strength}%
                                      </span>
                                    </div>
                                  </div>
                                )}
                            </div>

                            {/* Right Column - Notes and Hierarchy */}
                            <div className="space-y-3">
                              {contact.hierarchy_level !== null &&
                                contact.hierarchy_level !== undefined &&
                                contact.hierarchy_level > 0 && (
                                  <div>
                                    <span className="text-xs font-medium text-gray-500 uppercase">
                                      Hierarchy Level
                                    </span>
                                    <div className="text-sm">
                                      Level {contact.hierarchy_level}
                                    </div>
                                  </div>
                                )}
                              {contact.notes && (
                                <div>
                                  <span className="text-xs font-medium text-gray-500 uppercase">
                                    Notes
                                  </span>
                                  <div className="text-sm text-gray-700 line-clamp-3">
                                    {contact.notes}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Edit/Delete Actions */}
                          {(canEdit || canDelete) && (
                            <ItemActions
                              onEdit={() => {
                                setEditingContact(contact);
                                setContactForm({
                                  first_name: contact.first_name || '',
                                  last_name: contact.last_name || '',
                                  title: contact.title || '',
                                  email: contact.email || '',
                                  phone: contact.phone || '',
                                  department: contact.department || '',
                                  role_type: contact.role_type || '',
                                  influence_level:
                                    contact.influence_level || '',
                                  relationship_strength:
                                    contact.relationship_strength || 0,
                                  reports_to_contact_id:
                                    contact.reports_to_contact_id || '',
                                  hierarchy_level: contact.hierarchy_level || 0,
                                  notes: contact.notes || '',
                                });
                                setShowContactForm(true);
                              }}
                              onDelete={() => handleDeleteContact(contact.id)}
                            />
                          )}
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-12 w-12 mx-auto mb-4 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                    <p className="text-lg font-medium mb-2">No contacts yet</p>
                    <p className="text-sm">
                      Add your first contact to get started
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Stakeholder Map View */}
            {contactsView === 'stakeholder' && (
              <StakeholderMap
                contacts={contacts}
                onContactClick={(contact) => {
                  setEditingContact(contact);
                  setContactForm({
                    first_name: contact.first_name || '',
                    last_name: contact.last_name || '',
                    title: contact.title || '',
                    email: contact.email || '',
                    phone: contact.phone || '',
                    department: contact.department || '',
                    role_type: contact.role_type || '',
                    influence_level: contact.influence_level || '',
                    relationship_strength: contact.relationship_strength || 0,
                    reports_to_contact_id: contact.reports_to_contact_id || '',
                    hierarchy_level: contact.hierarchy_level || 0,
                    notes: contact.notes || '',
                  });
                  setShowContactForm(true);
                }}
              />
            )}

            {/* Org Chart View */}
            {contactsView === 'orgchart' && (
              <OrgChart
                contacts={contacts}
                onContactClick={(contact) => {
                  setEditingContact(contact);
                  setContactForm({
                    first_name: contact.first_name || '',
                    last_name: contact.last_name || '',
                    title: contact.title || '',
                    email: contact.email || '',
                    phone: contact.phone || '',
                    department: contact.department || '',
                    role_type: contact.role_type || '',
                    influence_level: contact.influence_level || '',
                    relationship_strength: contact.relationship_strength || 0,
                    reports_to_contact_id: contact.reports_to_contact_id || '',
                    hierarchy_level: contact.hierarchy_level || 0,
                    notes: contact.notes || '',
                  });
                  setShowContactForm(true);
                }}
              />
            )}
          </div>
        </div>
      )}
      {/* Planning Tab */}
      {activeTab === 'planning' && (
        <AccountPlanningDashboard
          companyId={id || ''}
          organizationId={selectedOrg?.id || ''}
          canEdit={canEdit}
          onAccountPlanUpdate={handleAccountPlanUpdate}
        />
      )}
    </div>
  );
}
